import React, { useCallback, useContext } from "react";
import { Box, Collapse } from "@chakra-ui/react";

import { useAnalytics } from "utils";
import { ConsentView } from "components/TOS/ConsentView";
import { TOSContext } from "./TOSProvider";
import { useTOSSignErrorCopy } from "./TOSStatusError";
import { useTOSGate } from "./useTOSGate";

/**
 * TOSConsent
 * @param {object} props
 * @param {boolean} [props.requireTOS=false] - opt in to gating. Without it this renders nothing and
 * makes no network call, which is what keeps out-of-scope surfaces on today's behavior despite the
 * app-wide provider.
 * @param {object} [props.copyProps] - Chakra style props for the consent SENTENCE, forwarded to
 * `ConsentView`. Nested rather than part of the spread so that how the copy is set stays consistent
 * across surfaces.
 * @param {...any} [boxProps] - Chakra style props for the consent BLOCK, for spacing it against the
 * field above. They must land on the block, not the copy: the sentence is an inline <span> inside
 * Chakra's checkbox label, where vertical padding paints but moves no layout. Applied inside the
 * <Collapse> so the spacing collapses away with the block.
 * @description Inline consent. Truth and presentation only - it never navigates, never renders a
 * submit button and never signs. The host reads `blockedByTOS` from its own `useTOSGate` call to
 * gate its CTA and awaits `signTOS()` at the top of its handler.
 *
 * Invisible unless consent is actually needed, so "resolving", "not required" and "status fetch
 * failed" all look the same - nothing - and the already-signed majority never sees a flash. On a
 * status failure rendering no consent UI is the correct outcome, not just the convenient one: a
 * ticked box that produces no record is the wrong artifact for provable consent. Hosts surface the
 * failure itself, either inline through <TOSStatusError /> or terminally through
 * <TOSStatusErrorEmptyState /> - see those two for which belongs where.
 */
export const TOSConsent = ({ requireTOS = false, copyProps, ...boxProps }) => {
  const analytics = useAnalytics();
  const tos = useContext(TOSContext);
  const { required, agreed, setAgreed, signError } = useTOSGate({ enabled: requireTOS });
  const { message: signErrorMessage } = useTOSSignErrorCopy();

  // Used directly as the callback ref: it is a stable useCallback that normalises `null`, and React
  // calls it with `null` on unmount, which is what clears the provider's ref. A stale ref to an
  // unmounted node would make a blocked click scroll nowhere.
  const consentRef = tos?.registerConsentNode;

  // No unmount teardown here: `agreed` is app-wide, and the provider clears it when the last gated
  // host unregisters - see registerGate in TOSProvider.

  const handleChange = useCallback(
    (event) => {
      const { checked } = event.target;
      setAgreed(checked);
      analytics?.track?.("TOS_JIT_Consent_Toggled", { agreed: checked });
    },
    [setAgreed, analytics],
  );

  // The only hard null: opting out is static, so there is nothing to animate in or out.
  if (!requireTOS) return null;

  return (
    // Mounted closed and opened by `in`, so the expansion animates and the iframe host gets a
    // clean resize - <Collapse in> mounted already-open does not transition.
    <Collapse in={required === true}>
      {/* pb first so a host spreading its own spacing can override it */}
      <Box ref={consentRef} data-testid="tos-consent" pb={4} {...boxProps}>
        <ConsentView
          checked={agreed}
          // ConsentView passes the raw event through for Formik's sake, so the boolean adaptation
          // happens on this side
          onChange={handleChange}
          // Supporting copy, not the screen's subject as it is on `/tos`. ConsentView carries these
          // onto the two legal links as well. Spread last so a host can override any of them.
          fontSize="xs"
          color="secondaryText"
          {...copyProps}
          // The localized sentence, never signError.message - see useTOSSignErrorCopy
          error={signError ? signErrorMessage : undefined}
        />
      </Box>
    </Collapse>
  );
};

export default TOSConsent;
